import { FunctionComponent, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  GuidReferenceReadModel,
  SecurityGroupRoleAssignmentsReadModel,
  StringReferenceReadModel,
} from "../../../../../../@api";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { useAppRolesService } from "../../../../../services/ServiceHooks/appRolesHook";
import { useSharedDialogState } from "../../../../Shared/Components/Dialogs/SharedDialog";
import { DropdownInputField, MultiSelectInputField } from "../../../../Shared/Form";
import { IManageSecurityGroupDialogState, IManageSecurityGroupFormValues } from "./manageSecurityGroupDialogTypes";

interface IManageSecurityGroupDialogContentProps {
  existingSecurityGroupIds?: string[];
  roles?: StringReferenceReadModel[];
  data?: SecurityGroupRoleAssignmentsReadModel;
}

const isSameFormData = (a: IManageSecurityGroupFormValues | undefined, b: IManageSecurityGroupFormValues): boolean => {
  const aSecurityGroupId = a?.securityGroup?.id ?? null;
  const bSecurityGroupId = b.securityGroup?.id ?? null;
  const aSecurityGroupDisplayName = a?.securityGroup?.displayName ?? "";
  const bSecurityGroupDisplayName = b.securityGroup?.displayName ?? "";
  const aAssignedRoleIds = a?.assignedRoleIds ?? [];
  const bAssignedRoleIds = b.assignedRoleIds;

  if (aSecurityGroupId !== bSecurityGroupId || aSecurityGroupDisplayName !== bSecurityGroupDisplayName) {
    return false;
  }

  if (aAssignedRoleIds.length !== bAssignedRoleIds.length) {
    return false;
  }

  return aAssignedRoleIds.every((roleId, index) => roleId === bAssignedRoleIds[index]);
};

export const ManageSecurityGroupDialogContent: FunctionComponent<IManageSecurityGroupDialogContentProps> = ({
  existingSecurityGroupIds,
  roles,
  data,
}: IManageSecurityGroupDialogContentProps) => {
  const { t } = useTranslation();
  const appRoleService = useAppRolesService();
  const { setSharedDialogState } = useSharedDialogState<IManageSecurityGroupDialogState>();

  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const addEditDialogTranslationsKeys = translationKeys.adminArea.appRoles.dialog;

  // creates the form controller with initial defaultValues from data
  const { control, trigger, getValues, formState, subscribe, setValue } = useForm<IManageSecurityGroupFormValues>({
    defaultValues: {
      securityGroup: data ? data.securityGroup : null,
      assignedRoleIds: data ? data.assignedRoleIds : [],
    },
    mode: "all", // runs validation on all interactions (change/blur/submit)
  });
  const watchedValues = useWatch({ control }); // watches current form values and re-renders when they change
  const normalizedFormData: IManageSecurityGroupFormValues = {
    // normalizes form values into a safe payload shape (converts watched values into the exact type the shared dialog footer expects)
    securityGroup: watchedValues?.securityGroup
      ? {
          id: watchedValues.securityGroup.id,
          displayName: watchedValues.securityGroup.displayName ?? "",
        }
      : null,
    assignedRoleIds: watchedValues?.assignedRoleIds ?? [],
  };
  const assignedRoleIdsKey = normalizedFormData.assignedRoleIds.join("|"); // turns role IDs array into a string

  // detects whether the user changed any field
  useEffect(() => {
    const unsubscribe = subscribe({
      formState: {
        // notifies when form values change
        values: true,
      },
      callback: ({ type }) => {
        // runs on form events
        if (type === "change") {
          setHasPendingChanges(true);
        }
      },
    });

    return () => {
      unsubscribe(); // removes the listener when dialog content unmounts
    };
  }, [subscribe]);

  const loadAvailableSecurityGroups = async (): Promise<StringReferenceReadModel[]> => {
    const securityGroupResponse = await appRoleService.getAvailableSecurityGroups();
    if (existingSecurityGroupIds) {
      return securityGroupResponse.entities?.filter(entity => !existingSecurityGroupIds.includes(entity.id!)) ?? [];
    }
    return securityGroupResponse.entities;
  };

  const loadAvailableRoles = async (): Promise<GuidReferenceReadModel[]> => {
    const roleResponse = await appRoleService.getAvailableRoles();
    return roleResponse.entities ?? [];
  };

  useEffect(() => {
    setSharedDialogState(previousState => {
      const isSameValidity = previousState.isValid === formState.isValid;
      const isSamePendingChanges = previousState.hasPendingChanges === hasPendingChanges;
      const isSamePayload = isSameFormData(previousState.formData, normalizedFormData);

      if (isSameValidity && isSamePendingChanges && isSamePayload) {
        return previousState;
      }

      return {
        ...previousState,
        isValid: formState.isValid,
        hasPendingChanges,
        formData: normalizedFormData,
      };
    });
  }, [
    formState.isValid,
    hasPendingChanges,
    normalizedFormData.securityGroup?.displayName,
    normalizedFormData.securityGroup?.id,
    assignedRoleIdsKey,
  ]);

  return (
    <div className="form-grid-layout">
      <div className="form-grid-column-span-4">
        <DropdownInputField
          fieldName="securityGroup"
          fieldNameTranslationKey={t(addEditDialogTranslationsKeys.form.securityGroup.label)}
          rules={{ required: true }}
          setValue={setValue}
          getValues={getValues}
          options={data ? [data.securityGroup] : undefined}
          disabled={!!data}
          trigger={trigger}
          control={control}
          placeholder={t(addEditDialogTranslationsKeys.form.securityGroup.placeholder)}
          loadOptions={data ? undefined : loadAvailableSecurityGroups}
        />
      </div>

      <div className="form-grid-column-span-4">
        <MultiSelectInputField
          fieldName="assignedRoleIds"
          fieldNameTranslationKey={t(addEditDialogTranslationsKeys.form.roles.label)}
          rules={{ required: !data }}
          trigger={trigger}
          setValue={setValue}
          getValues={getValues}
          options={roles?.length ? roles : undefined}
          control={control}
          optionValue="id"
          placeholder={t(addEditDialogTranslationsKeys.form.roles.placeholder)}
          loadOptions={data ? undefined : loadAvailableRoles}
        />
      </div>
    </div>
  );
};
